import { z } from "zod";
import {
	sanitizedEmailSchema,
	sanitizedNullableString,
	sanitizedOptionalString,
} from "../validation";

/**
 * Communication languages a user can pick as their preferred UI + email
 * language (`users.language`). Widened to include Swedish (sv) in PLA-427.
 * Note: `legalLocaleSchema` (organizations) stays `en | fi` because localized
 * legal documents only exist for those two locales.
 */
export const COMMUNICATION_LANGUAGES = ["en", "fi", "sv"] as const;
export const communicationLanguageSchema = z.enum(COMMUNICATION_LANGUAGES);
export type CommunicationLanguage = z.infer<typeof communicationLanguageSchema>;

const isValidTimeZone = (value: string): boolean => {
	try {
		new Intl.DateTimeFormat("en-US", { timeZone: value });
		return true;
	} catch {
		return false;
	}
};

export const userProfileUpdatesSchema = z
	.object({
		name: sanitizedOptionalString({ max: 120 }),
		email: sanitizedEmailSchema.optional(),
		phoneCountryCode: sanitizedNullableString({ max: 5 }),
		phoneNumber: sanitizedNullableString({ max: 32 }),
		locale: sanitizedOptionalString({ max: 8 }),
		language: communicationLanguageSchema.optional(),
		timezone: sanitizedNullableString({ max: 100 }).refine(
			(value) => value == null || isValidTimeZone(value),
			{ message: "Invalid timezone" },
		),
	})
	.strip();

const hasAtLeastOneValue = (value: Record<string, unknown>) =>
	Object.values(value).some((field) => field !== undefined);

export const nonEmptyUserProfileUpdatesSchema = userProfileUpdatesSchema.refine(
	hasAtLeastOneValue,
	{ message: "Provide at least one field to update" },
);

export const userSelfUpdateSchema = z
	.object({ updates: nonEmptyUserProfileUpdatesSchema })
	.strict();

export type UserProfileUpdatesInput = z.infer<typeof userProfileUpdatesSchema>;
